import type { TFunction } from 'react-i18next';
import type {
  CustomAction,
  BulkAction,
  BulkActionOfType,
} from '../singleactions/types';

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAsyncFn } from 'react-use';
import { useToast } from '__components/ToastProvider';
import { getErrorMessage } from 'hooks/useErrors';
import { getOriginalError } from 'services/AxiosService';
import PerformActionService from 'services/PerformActionService';
import { JSAction } from 'ts-components/Plugins/types';

type BulkActionSummaryRequest = {
  action:
    | Exclude<BulkAction['value'], 'custom_action'>
    | BulkActionOfType<'custom_action'>['customAction']['action'];
  automation_id?: string;
  custom_action_id?: string;
  custom_object_id?: string;
  field_id?: string;
  field_value?: any;
  group_id?: string;
  filters?: any;
  in_group_ids?: string[];
  record_ids?: string[];
  search_text?: string;
  subtract_record_ids?: string[];
  subscription_list_ids?: string[];
  subscription_list_status?: string;
  connector_id?: string;
};

type SummaryResult = {
  action: BulkAction['value'];
  custom_action?: {
    action: 'start_automation';
    automation: { id: string; api_name: string; name: string };
    custom_object: {
      id: string;
      name: string;
      object_name: string;
      entity_name: string;
    };
    description: string;
    id: string;
    name: string;
  };
  status: string;
  summary_id: string;
};

export type Summary = {
  action: BulkAction['value'];
  cache_key: null | string;
  errors: Record<string, any>;
  stats: {
    allowed_count: number;
    automations: null | {
      active_count: number;
      cancelled_count: number;
      completed_count: number;
      failed_count: number;
      paused_count: number;
    };
    limits: null | {
      additional_credits: number;
      additional_credits_budget: null | number;
      additional_credits_cost: {
        amount: string;
        currency: string;
        symbol: string;
      };
      additional_credits_needed: number;
      additional_credits_per_unit: number;
      additional_units_needed: number;
      additional_units_needed_cost: null | number;
      additional_units_to_be_purchased: number;
      additional_units_to_be_purchased_cost: null | number;
      base_monthly_limit: number;
      current_monthly_usage: number;
      insufficient_credits: number;
      remaining_monthly_limit: number;
      total_monthly_limit: number;
    };
    not_allowed_count: number;
    stages: unknown;
    subscriptions: null | {
      in_suppression_list: number;
      opted_out: number;
      unsubscribed: number;
    };
    total_count: number;
    valid_count: number;
  };
  status: 'completed' | 'failed' | 'initialize';
  summary_id: string;
};

export type UseGetSummaryOptions = {
  automation?: { label: string; value: string };
  customAction?: CustomAction;
  field?: Record<string, any>;
  fieldValue?: any;
  filters?: any;
  groupId?: string;
  groupOptions?: [
    { label: string; value: 'none' },
    ...{
      config: any;
      employee_access: string;
      id: string;
      label: string;
      name: string;
      value: string;
    }[],
  ];
  objectId?: string;
  performActionOptions?: BulkAction[];
  records?: any[];
  search?: string;
  secondaryAction?: 'start' | 'pause' | 'cancel';
  selectedAction: Pick<BulkAction, 'value'>;
  selection: {
    checked?: boolean;
    checkedCount?: number;
    checkedIds: string[];
    subtractIds?: string[];
    value: 'all' | 'group' | 'individual' | 'page' | 'search' | 'none';
  };
  status?: string;
  subscriptionItem?: { label: string; value: string };
  subscriptionListOptions?: { label: string; value: string }[];
  subscriptionLists?: string[];
  smartConnector?: { label: string; value: string };
};

export const SECONDARY_ACTIONS = {
  START: 'start',
  PAUSE: 'pause',
  CANCEL: 'cancel',
};

export const BULK_ACTION_TYPES = {
  page: 'page',
  search: 'search',
  group: 'group',
  all: 'all',
  individual: 'individual',
  none: 'none',
};

export const SEND_EMAIL_IDENTIFIER = 'send_email';
export const SEND_TEXT_IDENTIFIER = 'send_text';
export const SEND_SURVEY_IDENTIFIER = 'send_survey';
export const CHANGE_TAGS_IDENTIFIER = 'change_tags';
export const MODIFY_AUTOMATION_IDENTIFIER = 'start_automation';
export const START_AGENTIC_WORKFLOW_IDENTIFIER = 'start_agentic_workflow';
export const MANAGE_TEAM_IDENTIFIER = 'manage_team';
export const MANAGE_SUBSCRIPTIONS = 'manage_subscription';
export const EXPORT_IDENTIFIER = 'export';
export const ARCHIVE_IDENTIFIER = 'archive';
export const CHANGE_FIELD_IDENTIFIER = 'change_field_value';
export const CUSTOM_ACTION_IDENTIFIER = 'custom_action';
export const JS_ACTION_IDENTIFIER = 'js_action';
export const SMART_CONNECTOR_IDENTIFIER = 'start_smart_connector';

// Action group definitions used for the grouped modal and dropdown menu
export const AGENTIC_WORKFLOWS_ACTION_IDENTIFIERS: string[] = [
  MODIFY_AUTOMATION_IDENTIFIER,
  SMART_CONNECTOR_IDENTIFIER,
  CUSTOM_ACTION_IDENTIFIER,
  JS_ACTION_IDENTIFIER,
];

export const MANAGE_RECORDS_ACTION_IDENTIFIERS: string[] = [
  ARCHIVE_IDENTIFIER,
  CHANGE_FIELD_IDENTIFIER,
  EXPORT_IDENTIFIER,
  MANAGE_TEAM_IDENTIFIER,
];

export const COMMUNICATION_ACTION_IDENTIFIERS: string[] = [
  SEND_EMAIL_IDENTIFIER,
  SEND_TEXT_IDENTIFIER,
  SEND_SURVEY_IDENTIFIER,
  CHANGE_TAGS_IDENTIFIER,
  MANAGE_SUBSCRIPTIONS,
];

export const CONTACTS_AGENTIC_WORKFLOW_IDENTIFIERS: string[] = [
  MODIFY_AUTOMATION_IDENTIFIER,
  START_AGENTIC_WORKFLOW_IDENTIFIER,
  SMART_CONNECTOR_IDENTIFIER,
  CUSTOM_ACTION_IDENTIFIER,
  JS_ACTION_IDENTIFIER,
];

export const CONTACTS_MANAGE_CONTACTS_IDENTIFIERS: string[] = [
  SEND_EMAIL_IDENTIFIER,
  SEND_TEXT_IDENTIFIER,
  CHANGE_TAGS_IDENTIFIER,
  CHANGE_FIELD_IDENTIFIER,
  MANAGE_TEAM_IDENTIFIER,
  MANAGE_SUBSCRIPTIONS,
  EXPORT_IDENTIFIER,
  ARCHIVE_IDENTIFIER,
];

export const getContactsModalGroupDefs = (t: TFunction) => [
  {
    label: t('Agentic Workflows'),
    key: 'agentic_workflow',
    identifiers: CONTACTS_AGENTIC_WORKFLOW_IDENTIFIERS,
  },
  {
    label: t('Manage Contacts'),
    key: 'manage_contacts',
    identifiers: CONTACTS_MANAGE_CONTACTS_IDENTIFIERS,
  },
];

export const getContactsGroupedDropdownOptions = (
  performActionOptions: BulkAction[],
  t: TFunction
) => {
  const groupDefs = [
    {
      label: t('Agentic Workflows'),
      identifiers: CONTACTS_AGENTIC_WORKFLOW_IDENTIFIERS,
    },
    {
      label: t('Manage Contacts'),
      identifiers: CONTACTS_MANAGE_CONTACTS_IDENTIFIERS,
    },
  ];

  const allGroupedIdentifiers = groupDefs.flatMap((g) => g.identifiers);

  const groups = groupDefs
    .map(({ label, identifiers }) => ({
      label,
      options: identifiers.flatMap((id) =>
        performActionOptions.filter((opt) => opt.value === id)
      ),
    }))
    .filter((g) => g.options.length > 0);

  const ungrouped = performActionOptions.filter(
    (opt) => !allGroupedIdentifiers.includes(opt.value)
  );

  if (ungrouped.length > 0) {
    groups.push({ label: t('Other'), options: ungrouped });
  }

  return groups;
};

export const BULK_ACTION_TO_BULK_PERMISSION = {
  [SEND_EMAIL_IDENTIFIER]: 'bulk_send_email',
  [SEND_TEXT_IDENTIFIER]: 'bulk_send_text',
  [SEND_SURVEY_IDENTIFIER]: 'bulk_send_survey',
  [CHANGE_TAGS_IDENTIFIER]: 'bulk_change_tags',
  [MODIFY_AUTOMATION_IDENTIFIER]: 'bulk_start_automation',
  [MANAGE_TEAM_IDENTIFIER]: 'bulk_manage_associations',
  [MANAGE_SUBSCRIPTIONS]: 'bulk_manage_subscriptions',
  [EXPORT_IDENTIFIER]: 'bulk_export_to_csv',
  [ARCHIVE_IDENTIFIER]: 'bulk_archive',
  [CHANGE_FIELD_IDENTIFIER]: 'bulk_change_field_value',
  [SMART_CONNECTOR_IDENTIFIER]: 'bulk_start_smart_connector',
};

export const doesActionGenerateFile = (action: BulkAction['value']) => {
  return action === EXPORT_IDENTIFIER;
};

export const getGroupedDropdownOptions = (
  performActionOptions: BulkAction[],
  t: TFunction
) => {
  const groupDefs = [
    {
      label: t('Agentic Workflows'),
      identifiers: AGENTIC_WORKFLOWS_ACTION_IDENTIFIERS,
    },
    {
      label: t('Manage Records'),
      identifiers: MANAGE_RECORDS_ACTION_IDENTIFIERS,
    },
    {
      label: t('Communication'),
      identifiers: COMMUNICATION_ACTION_IDENTIFIERS,
    },
  ];

  const allGroupedIdentifiers = groupDefs.flatMap((g) => g.identifiers);

  const groups = groupDefs
    .map(({ label, identifiers }) => ({
      label,
      options: identifiers.flatMap((id) =>
        performActionOptions.filter((opt) => opt.value === id)
      ),
    }))
    .filter((g) => g.options.length > 0);

  const ungrouped = performActionOptions.filter(
    (opt) => !allGroupedIdentifiers.includes(opt.value)
  );

  if (ungrouped.length > 0) {
    groups.push({ label: t('Other'), options: ungrouped });
  }

  return groups;
};

export const getCustomActionOptions = (
  customActions: CustomAction[],
  t: TFunction
): BulkActionOfType<'custom_action'>[] => {
  return customActions.map((customAction) => ({
    value: CUSTOM_ACTION_IDENTIFIER,
    label: customAction.name,
    steps: [{ title: t('Choose Action') }, { title: t('Confirm') }],
    titleForModal: t('{{name}} on', customAction),
    customAction,
  }));
};

export const getJsActionOptions = (
  jsActions: JSAction[],
  t: TFunction
): BulkActionOfType<'js_action'>[] => {
  return jsActions
    .filter((action) => action.includePerformAction)
    .sort((a, b) => String(a.name).localeCompare(String(b.name)))
    .map((jsAction) => ({
      value: JS_ACTION_IDENTIFIER,
      label: jsAction.name,
      steps: [{ title: t('Choose Action') }, { title: t('Confirm') }],
      titleForModal: t('{{name}} on', jsAction),
      jsAction,
    }));
};

export const getPerformActionOptions = (
  t: TFunction,
  entityNamePlural = 'Contacts'
): BulkAction[] => [
  {
    value: SEND_EMAIL_IDENTIFIER,
    label: t('Send Email'),
    steps: [
      { title: t('Choose Action') },
      { title: t('Send Email') },
      { title: t('Confirm') },
    ],
    titleForModal: t('Send Email to'),
  },
  {
    value: SEND_TEXT_IDENTIFIER,
    label: t('Send Text'),
    steps: [
      { title: t('Choose Action') },
      { title: t('Send Text') },
      { title: t('Confirm') },
    ],
    titleForModal: t('Send Text to'),
  },
  {
    value: CHANGE_TAGS_IDENTIFIER,
    label: t('Change Tags'),
    steps: [
      { title: t('Choose Action') },
      { title: t('Change Tags') },
      { title: t('Confirm') },
    ],
    titleForModal: t('Change Tags on'),
  },
  {
    value: CHANGE_FIELD_IDENTIFIER,
    label: t('Change Field Value'),
    steps: [
      { title: t('Choose Action') },
      { title: t('Change Field Value') },
      { title: t('Confirm') },
    ],
    titleForModal: t('Change Field Value on'),
  },
  {
    value: MODIFY_AUTOMATION_IDENTIFIER,
    label: t('Modify Agentic Workflow'),
    steps: [
      { title: t('Choose Action') },
      { title: t('Modify Agentic Workflow') },
      { title: t('Confirm') },
    ],
    titleForModal: t('Modify Agentic Workflow on'),
  },
  {
    value: MANAGE_TEAM_IDENTIFIER,
    label: t('Manage Team'),
    steps: [
      { title: t('Choose Action') },
      { title: t('Manage Team') },
      { title: t('Confirm') },
    ],
    titleForModal: t('Manage Team on'),
  },
  {
    value: MANAGE_SUBSCRIPTIONS,
    label: t('Manage Subscriptions'),
    steps: [
      { title: t('Choose Action') },
      { title: t('Manage Subscriptions') },
      { title: t('Confirm') },
    ],
    titleForModal: t('Manage Subscription Lists on'),
  },
  {
    value: EXPORT_IDENTIFIER,
    label: t('Export to CSV'),
    steps: [
      { title: t('Choose Action') },
      { title: t('Export Options') },
      { title: t('Confirm') },
    ],
    titleForModal: t('Export'),
  },
  {
    value: ARCHIVE_IDENTIFIER,
    label: `${t('Archive')} ${entityNamePlural}`,
    steps: [{ title: t('Choose Action') }, { title: t('Confirm') }],
    titleForModal: t('Archive'),
  },
  {
    value: SMART_CONNECTOR_IDENTIFIER,
    label: t('Run SmartConnector'),
    steps: [{ title: t('Choose Action') }, { title: t('Confirm') }],
    titleForModal: t('Run SmartConnector on'),
  },
];

const getAction = (options: {
  selectedActionValue?: Exclude<BulkAction['value'], 'custom_action'>;
  performActionOptions?: BulkAction[];
  secondaryAction?: string;
}): BulkActionSummaryRequest['action'] => {
  const { selectedActionValue, performActionOptions, secondaryAction } =
    options;

  if (selectedActionValue === MODIFY_AUTOMATION_IDENTIFIER) {
    return `${secondaryAction || 'start'}_automation` as BulkActionSummaryRequest['action'];
  }
  if (selectedActionValue === START_AGENTIC_WORKFLOW_IDENTIFIER) {
    return 'start_automation';
  }

  if (selectedActionValue) {
    return selectedActionValue;
  }

  // we now look at the available options
  if (performActionOptions?.length) {
    // we would prefer not to use email or text as they may a have a lesser count
    const preferedActions = performActionOptions.filter(
      ({ value }) => !['bulk_send_email', 'bulk_send_text'].includes(value)
    );

    return (
      preferedActions?.length
        ? preferedActions[0].value
        : performActionOptions[0].value
    ) as BulkActionSummaryRequest['action'];
  }

  // a default
  return 'change_field_value';
};

export const POLLING_TIMEOUTS = [1000, 3000, 5000, 7000, 9000];

// BE filter priority is: group > filters
export const useGetSummary = (
  options: UseGetSummaryOptions
): [
  { summary: Summary | null; isFetching: boolean; isPolling: boolean },
  () => Promise<void>,
] => {
  const {
    objectId,
    selection,
    records,
    groupOptions,
    groupId,
    search,
    filters,
    field,
    fieldValue,
    selectedAction,
    performActionOptions,
    subscriptionLists,
    subscriptionListOptions,
    status,
    subscriptionItem,
    secondaryAction,
    automation,
    customAction,
    smartConnector,
  } = options;

  const { t } = useTranslation();
  const [showToast] = useToast();
  const [isPolling, setIsPolling] = useState(false);
  const [startSummaryResult, setStartSummaryResult] =
    useState<SummaryResult | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);

  const [{ loading: isFetching }, startGetSummary] = useAsyncFn(async () => {
    try {
      const commonBody: BulkActionSummaryRequest = {
        custom_object_id: objectId,
        subtract_record_ids: selection.subtractIds,
        field_id: field?.id,
        field_value: fieldValue?.code,
        automation_id: automation?.value || customAction?.automation?.id,
        connector_id: smartConnector?.value,
        action:
          selectedAction?.value === CUSTOM_ACTION_IDENTIFIER
            ? customAction?.action!
            : getAction({
                selectedActionValue: selectedAction?.value,
                performActionOptions,
                secondaryAction,
              }),
      };

      if (selectedAction?.value === CUSTOM_ACTION_IDENTIFIER) {
        commonBody.custom_action_id = customAction?.id;
      }

      // Handle params for subscription list selection
      if (
        selectedAction?.value === SEND_EMAIL_IDENTIFIER &&
        subscriptionLists
      ) {
        if (subscriptionLists.length) {
          commonBody.subscription_list_ids = subscriptionLists;
        } else {
          // An empty selection means "all subscription lists"
          commonBody.subscription_list_ids = subscriptionListOptions?.map(
            (opt) => opt.value
          );
        }
      }
      // special summary request for MANAGE_SUBSCRIPTIONS
      if (
        selectedAction?.value === MANAGE_SUBSCRIPTIONS &&
        status &&
        subscriptionItem
      ) {
        commonBody.subscription_list_ids = [subscriptionItem.value];
        commonBody.subscription_list_status = status;
      }

      if (selection.value === 'page' && records) {
        commonBody.record_ids = selection.checkedIds;
      } else if (selection.value === 'group') {
        const group = groupOptions?.find(
          (el) => 'id' in el && el.id === groupId
        );
        commonBody.in_group_ids = group && 'id' in group ? [group?.id] : [];
      } else if (selection.value === 'search' && selection.checkedCount) {
        commonBody.search_text = search || undefined;
        commonBody.filters = filters;
      } else if (
        selection.value === 'individual' &&
        selection.checkedIds.length !== 0
      ) {
        commonBody.record_ids = selection.checkedIds;
      }

      const response: SummaryResult =
        await PerformActionService.startSummary(commonBody);
      const result =
        selectedAction?.value === CUSTOM_ACTION_IDENTIFIER
          ? {
              ...response,
              action: response?.action || CUSTOM_ACTION_IDENTIFIER,
            }
          : response;
      setIsPolling(true);
      setStartSummaryResult(result);
      return result;
    } catch (error) {
      showToast({
        message:
          getErrorMessage(getOriginalError(error)) ||
          t('An error occurred. Please try again or contact support.'),
        variant: 'failure',
      });
      setIsPolling(false);
      setStartSummaryResult(null);
      throw error;
    }
  }, [
    objectId,
    selection,
    records,
    groupOptions,
    groupId,
    search,
    filters,
    field?.id,
    fieldValue?.code,
    automation?.value,
    selectedAction?.value,
    performActionOptions,
    subscriptionLists,
    subscriptionListOptions,
    status,
    subscriptionItem,
    secondaryAction,
    customAction?.id,
    smartConnector?.value,
  ]);

  const getSummaryFn = useCallback(async () => {
    if (
      startSummaryResult?.action &&
      startSummaryResult.action === selectedAction?.value &&
      startSummaryResult?.custom_action?.id &&
      startSummaryResult.custom_action.id === customAction?.id
    ) {
      return;
    } else {
      await startGetSummary();
    }
  }, [
    startGetSummary,
    startSummaryResult?.action,
    startSummaryResult?.custom_action?.id,
    selectedAction?.value,
    customAction?.id,
  ]);

  useEffect(() => {
    setStartSummaryResult(null);
  }, [startGetSummary]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    let reset = false;
    setSummary(null);

    if (startSummaryResult?.summary_id) {
      setIsPolling(true);

      let count = 0;

      const poll = async () => {
        try {
          const summaryResult = await PerformActionService.getSummary(
            startSummaryResult.summary_id
          );

          if (reset) {
            return;
          }

          switch (summaryResult.status) {
            case 'completed':
              clearTimeout(timeout);
              setSummary(summaryResult);
              setIsPolling(false);
              break;
            case 'failed':
              clearTimeout(timeout);
              setSummary(null);
              setIsPolling(false);
              showToast({
                message:
                  getErrorMessage(summaryResult.errors) ||
                  t('An error occurred. Please try again or contact support.'),
                variant: 'failure',
              });
              break;
            default:
              timeout = setTimeout(poll, POLLING_TIMEOUTS[count++] || 10_000);
          }
        } catch (error) {
          clearTimeout(timeout);
          setIsPolling(false);
          setSummary(null);
          showToast({
            message:
              getErrorMessage(getOriginalError(error)) ||
              t('An error occurred. Please try again or contact support.'),
            variant: 'failure',
          });
        }
      };

      poll();
    } else {
      setIsPolling(false);
    }

    return () => {
      clearTimeout(timeout);
      setIsPolling(false);
      reset = true;
    };
  }, [startSummaryResult?.summary_id, showToast, t]);

  return [{ summary, isFetching, isPolling }, getSummaryFn];
};

export const moneyFormatter = (num: number) =>
  Intl.NumberFormat('en', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(num);

export const numberFormatter = (num: number) =>
  Intl.NumberFormat('en').format(num);
